import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { RootState } from '../../app/store'
import {
  isArabicText,
  isEnglishText,
  translatePaymentMethod,
  getCorrectPrice,
  getTextAlignment,
} from '../../utils/receiptHelper'
import { getCurrentDesign } from '../../services/settingsService'
import { useCountryConfig } from '../../hooks/useCountryConfig'
import { isEgyptCountry } from '../../utils/currencyUtils'
import logoPrint from '../../assets/images/logo_new_en.svg'
import { parseOrderPromotion } from '../../features/offers/orderPromotion'
import { parseServiceFeePercentage, parseServicePrice } from '../../utils'

interface ReceiptProps {
  order: any
  isRTL: boolean
}

const Receipt: React.FC<ReceiptProps> = ({ order, isRTL }) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.language || 'ar'
  const countryConfig = useCountryConfig()
  const settingsServiceFeePercentage = useSelector((state: RootState) => state.settings.orderServiceFeePercentage)
  
  // Logo from API design settings (same as TopBar)
  const [headerLogoUrl, setHeaderLogoUrl] = useState<string | undefined>(() => getCurrentDesign().headerLogo)

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { headerLogo?: string } | undefined
      if (detail && detail.headerLogo) {
        setHeaderLogoUrl(detail.headerLogo)
      }
    }
    window.addEventListener('designSettingsApplied', handler as (event: Event) => void)
    return () => window.removeEventListener('designSettingsApplied', handler as (event: Event) => void)
  }, [])

  // Format date and time to match Flutter format
  // Handles API format: "2025-11-30 13:52:29"
  const formatDateTime = (dateString?: string) => {
    if (!dateString) {
      return new Date().toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      })
    }
    // Parse the date string - handles "2025-11-30 13:52:29" format from API
    const date = new Date(dateString.replace(' ', 'T')) // Convert space to T for ISO format
    // Check if date is valid
    if (isNaN(date.getTime())) {
      // Fallback: try parsing as-is
      const fallbackDate = new Date(dateString)
      if (isNaN(fallbackDate.getTime())) {
        return dateString // Return original string if parsing fails
      }
      return fallbackDate.toLocaleString('ar-SA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      })
    }
    return date.toLocaleString('ar-SA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    })
  }

  // Extract order data
  const orderNumber =
    order?.id?.toString() ||
    order?.order_number?.toString() ||
    order?.order_id?.toString() ||
    ''

  // Get created_at from API - display as-is without formatting
  const dateTime = order?.created_at || order?.createdAt || order?.date || order?.dateTime || ''

  // Customer phone
  const phoneNumber =
    order?.user?.phone ||
    order?.customer?.phone ||
    order?.customer_phone ||
    order?.phoneNumber ||
    order?.customer?.phone_number ||
    ''

  // Payment method - get from API, check multiple possible formats
  const paymentMethodRaw =
    order?.payment_method?.name ||
    order?.payment_method ||
    order?.paymentMethod ||
    order?.paymentMethodText ||
    ''

  // Only translate if we have a value, otherwise show empty or default
  const paymentMethod = paymentMethodRaw ? translatePaymentMethod(paymentMethodRaw, locale) : ''

  // Branch - handle both string and object cases
  const branch = typeof order?.branch === 'string'
    ? order.branch
    : (order?.branch?.name || order?.branchName || '')

  // Order Type - get from order (supports multiple formats)
  const orderTypeRaw = 
    order?.typeText ||  // From OrderDetailsPage
    order?.order_type ||
    order?.orderType ||
    order?.type ||
    ''
  
  // If we have typeId but no typeText, map it
  const orderTypeFromId = order?.typeId ? (() => {
    const typeId = order.typeId
    switch (typeId) {
      case 1: return locale === 'ar' ? 'توصيل' : 'Delivery'
      case 2: return locale === 'ar' ? 'استلام' : 'Takeaway'
      case 3: return locale === 'ar' ? 'في الفرع' : 'Dine In'
      case 4: return locale === 'ar' ? 'في السيارة' : 'Car Pickup'
      default: return ''
    }
  })() : ''
  
  // Translate order type to Arabic/English
  const translateOrderType = (type: string, locale: string): string => {
    const typeLower = type.toLowerCase()
    if (locale === 'ar') {
      switch (typeLower) {
        case 'delivery':
        case 'توصيل':
          return 'توصيل'
        case 'takeaway':
        case 'take away':
        case 'سفري':
        case 'استلام':
          return 'استلام'
        case 'dine_in':
        case 'dine-in':
        case 'dine in':
        case 'محلي':
        case 'في الفرع':
          return 'في الفرع'
        case 'car_pickup':
        case 'car pickup':
        case 'استلام من السيارة':
        case 'في السيارة':
          return 'في السيارة'
        default:
          return type
      }
    } else {
      switch (typeLower) {
        case 'delivery':
        case 'توصيل':
          return 'Delivery'
        case 'takeaway':
        case 'take away':
        case 'سفري':
          return 'Takeaway'
        case 'dine_in':
        case 'dine-in':
        case 'dine in':
        case 'محلي':
          return 'Dine In'
        case 'car_pickup':
        case 'car pickup':
        case 'استلام من السيارة':
          return 'Car Pickup'
        default:
          return type
      }
    }
  }

  const orderType = orderTypeFromId || (orderTypeRaw ? translateOrderType(orderTypeRaw, locale) : '')

  // Table Number - get from order (supports multiple formats)
  // Hide if undefined, null, empty, or the string "undefined"
  const tableNumberRaw = 
    order?.table?.name ||      // From OrderDetailsPage
    order?.table?.number ||
    order?.table?.id ||
    order?.table_number ||
    order?.tableNumber ||
    order?.table_id ||
    ''
  
  const tableNumber = (tableNumberRaw && 
                       tableNumberRaw !== 'undefined' && 
                       String(tableNumberRaw).trim() !== '') 
    ? String(tableNumberRaw) 
    : null

  // Notes
  const notes =
    order?.notes ||
    order?.order_notes ||
    ''

  // Creator/Cashier name
  const creatorName =
    (typeof order?.creator === 'object' && order?.creator?.name) ||
    (typeof order?.creator === 'string' ? order?.creator : '') ||
    (typeof order?.cashier === 'object' && order?.cashier?.name) ||
    (typeof order?.cashier === 'string' ? order?.cashier : '') ||
    order?.created_by_name ||
    ''

  // Extract items
  const items = order?.items || order?.meals || order?.order_items || []

  // Calculate subtotal from items if available, otherwise use order amount
  const calculateSubtotalFromItems = (): number => {
    if (!items || items.length === 0) return 0
    return items.reduce((sum: number, item: any) => {
      // Use total_price or total_unit_price if available
      const itemTotal = parseFloat(
        String(
          item.total_price ||
          item.total_unit_price ||
          item.totalUnitPrice ||
          (item.price && item.quantity ? item.price * item.quantity : 0) ||
          0
        )
      )
      return sum + (isNaN(itemTotal) ? 0 : itemTotal)
    }, 0)
  }

  // Calculate totals - prefer order.amount, then calculate from items, then fallback to subtotal
  const calculatedSubtotal = calculateSubtotalFromItems()
  const subtotal = parseFloat(
    String(
      order?.amount || // Use amount from API first
      (calculatedSubtotal > 0 ? calculatedSubtotal : null) || // Then calculated from items
      order?.subtotal ||
      order?.sub_total ||
      order?.price_before_discount ||
      '0'
    )
  )

  const taxAmount = parseFloat(
    String(order?.taxValue || order?.tax_value || order?.tax_amount || order?.tax || '0')
  )

  const serviceFee = parseServicePrice(order)
  const serviceFeePercentage = parseServiceFeePercentage(order) ?? settingsServiceFeePercentage

  const total = parseFloat(
    String(order?.finalTotal || order?.final_total || order?.total_price || order?.total_amount || '0')
  )

  // Extract delivery price from multiple possible locations
  let deliveryFeeValue: any = null
  if (order?.deliveryPrice !== undefined && order?.deliveryPrice !== null) {
    deliveryFeeValue = order.deliveryPrice
  } else if (order?.delivery_price !== undefined && order?.delivery_price !== null) {
    deliveryFeeValue = order.delivery_price
  }
  // Parse to number, default to 0 if null (to allow showing 0.00 for type_id = 1)
  const deliveryFee = deliveryFeeValue !== null ? parseFloat(String(deliveryFeeValue)) : 0

  // Get order type_id to determine if we should show table_price and delivery_price
  const orderTypeId = order?.type_id || order?.typeId || order?.type?.id
  const orderTypeIdNum = orderTypeId !== undefined && orderTypeId !== null
    ? (typeof orderTypeId === 'string' ? parseInt(orderTypeId) : Number(orderTypeId))
    : null
  
  // Extract table price from multiple possible locations
  // Check order.table.table_price first, then fallback to other locations
  let tableFeeValue: any = null
  if (order?.table?.table_price !== undefined && order?.table?.table_price !== null) {
    tableFeeValue = order.table.table_price
  } else if (order?.tablePrice !== undefined && order?.tablePrice !== null) {
    tableFeeValue = order.tablePrice
  } else if (order?.table_price !== undefined && order?.table_price !== null) {
    tableFeeValue = order.table_price
  }
  // Parse to number, default to 0 if null (to allow showing 0.00 for type_id = 3)
  const tableFee = tableFeeValue !== null ? parseFloat(String(tableFeeValue)) : 0
  
  // Show table_price only if type_id is 3 (محلي), even if zero
  const shouldShowTablePrice = orderTypeIdNum === 3
  // Show delivery_price only if type_id is 1 (تواصيل), even if zero
  const shouldShowDeliveryPrice = orderTypeIdNum === 1

  const discountAmount = order?.discount
    ? parseFloat(String(order.discount))
    : null

  const orderPromotion = useMemo(
    () => parseOrderPromotion(order?.promotion),
    [order?.promotion],
  )

  const promotionDiscount =
    orderPromotion?.discount != null && orderPromotion.discount > 0
      ? orderPromotion.discount
      : 0
  const promotionCashback =
    orderPromotion?.cashback != null && orderPromotion.cashback > 0
      ? orderPromotion.cashback
      : 0
  const promotionSetItemSavings =
    (orderPromotion?.set_item_price_savings ??
      orderPromotion?.setItemPriceSavings ??
      0) > 0
      ? (orderPromotion?.set_item_price_savings ??
        orderPromotion?.setItemPriceSavings ??
        0)
      : 0

  const hasPromotionDiscountRow =
    promotionDiscount > 0 || promotionSetItemSavings > 0

  // Get translated text - ensure it always returns a translation
  const getTranslatedText = (key: string) => {
    const translation = t(key)
    // If translation returns the key itself (no translation found), try with fallback
    if (translation === key) {
      // Try common fallbacks
      const fallbacks: Record<string, string> = {
        'receipt_order_number': locale === 'ar' ? 'رقم الطلب' : 'Order Number',
        'receipt_customer_phone': locale === 'ar' ? 'رقم هاتف العميل' : 'Customer Phone',
        'receipt_payment_method': locale === 'ar' ? 'طريقة الدفع' : 'Payment Method',
        'receipt_branch': locale === 'ar' ? 'الفرع' : 'Branch',
        'receipt_order_type': locale === 'ar' ? 'نوع الطلب' : 'Order Type',
        'receipt_table_number': locale === 'ar' ? 'رقم الطاولة' : 'Table Number',
        'receipt_products': locale === 'ar' ? 'المنتجات' : 'Products',
        'receipt_quantity': locale === 'ar' ? 'الكمية' : 'Quantity',
        'receipt_price': locale === 'ar' ? 'السعر' : 'Price',
        'receipt_subtotal': locale === 'ar' ? 'المجموع الفرعي' : 'Subtotal',
        'receipt_tax': locale === 'ar' ? 'الضريبة' : 'Tax',
        'receipt_total': locale === 'ar' ? 'المجموع الكلي' : 'Total',
        'receipt_currency': locale === 'ar' ? 'ريال' : 'SAR',
        'receipt_powered_by': locale === 'ar' ? 'مدعوم من' : 'Powered by',
        'receipt_delivery_fee': locale === 'ar' ? 'رسوم التوصيل' : 'Delivery Fee',
        'receipt_table_fee': locale === 'ar' ? 'رسوم الطاولة' : 'Table Fee',
        'receipt_service_fee': locale === 'ar' ? 'رسوم الخدمة' : 'Service Fee',
        'receipt_discount': locale === 'ar' ? 'الخصم' : 'Discount',
        'receipt_notes': locale === 'ar' ? 'الملاحظات' : 'Notes',
        'receipt_cashier': locale === 'ar' ? 'الكاشير' : 'Cashier',
      }
      return fallbacks[key] || translation
    }
    return translation
  }

  const getReceiptCurrencyLabel = () =>
    isEgyptCountry(countryConfig)
      ? t('currency.egyptSymbol')
      : getTranslatedText('receipt_currency')

  // Build dashed separator line - optimized for thermal paper
  const DashedSeparator = () => (
    <div
      className="w-full h-0.5 my-2 pl-15 pr-15"
      style={{
        backgroundImage: 'repeating-linear-gradient(to right, #808080 0px, #808080 5px, transparent 5px, transparent 8px)',
      }}
    />
  )

  // Build header section - show logo from API with date/time below
  const buildHeader = () => {
    // dateTime is already calculated above from order data (using created_at from API)
    return (
      <div className="flex flex-col items-center w-full">
        <img 
          src={headerLogoUrl || logoPrint} 
          alt="brand logo" 
          className="h-16 w-auto object-contain max-w-full" 
        />
        <div className="h-2" />
        <span
          className="text-sm font-semibold text-black"
          style={{ fontFamily: "'IBM Plex Sans Arabic', sans-serif" }}
        >
          {dateTime}
        </span>
      </div>
    )
  }

  // Build order number box
  const buildOrderNumberBox = () => {
    const orderNumberLabel = getTranslatedText('receipt_order_number')
    const isLabelEnglish = isEnglishText(orderNumberLabel)

    return (
      <div className="w-full pl-15 pr-3 border-1 border-gray-400 rounded-lg">
        <div className="flex items-center justify-center gap-3">
          <span
            className="text-base font-semibold text-black"
            style={{
              fontFamily: isArabicText(orderNumberLabel)
                ? "'IBM Plex Sans Arabic', sans-serif"
                : "'Roboto', sans-serif",
            }}
          >
            {orderNumberLabel}
          </span>
          <span
            className="text-2xl font-bold text-black"
            style={{ fontFamily: "'Roboto', sans-serif" }}
          >
            {orderNumber}#
          </span>
        </div>
      </div>
    )
  }

  // Build detail row - Arabic: label on right, value on left | English: label on left, value on right
  const buildDetailRow = (label: string, value: string) => {
    const isLabelArabic = isArabicText(label)
    const isValueArabic = isArabicText(value)

    // In Arabic (RTL): label on right, value on left (original design)
    // In English (LTR): label on left, value on right (reversed)
    return (
      <div className={`flex justify-between items-center w-full flex-row-reverse`}>
       
        <span
          className="text-base font-semibold text-black"
          style={{
            fontFamily: isValueArabic
              ? "'IBM Plex Sans Arabic', sans-serif"
              : "'Roboto', sans-serif",
          }}
        >
          {value}
        </span> 
        <span
          className="text-base font-semibold text-black"
          style={{
            fontFamily: isLabelArabic
              ? "'IBM Plex Sans Arabic', sans-serif"
              : "'Roboto', sans-serif",
          }}
        >
          {label}
        </span>
      </div>
    )
  }

  // Build details section - optimized for thermal paper
  const buildDetailsSection = () => (
    <div className="flex flex-col gap-0 w-full">
      {creatorName && (
        <>
          {buildDetailRow(getTranslatedText('receipt_cashier'), creatorName)}
          <div className="h-2" />
        </>
      )}
      {buildDetailRow(
        getTranslatedText('receipt_customer_phone'),
        phoneNumber || 'undefined'
      )}
      <div className="h-2" />
      {paymentMethod && paymentMethod.trim() !== '' && (
        <>
          {buildDetailRow(
            getTranslatedText('receipt_payment_method'),
            paymentMethod
          )}
          <div className="h-2" />
        </>
      )}
      {buildDetailRow(getTranslatedText('receipt_branch'), branch || '')}
      {orderType && (
        <>
          <div className="h-2" />
          {buildDetailRow(getTranslatedText('receipt_order_type'), orderType)}
        </>
      )}
      {tableNumber && (
        <>
          <div className="h-2" />
          {buildDetailRow(getTranslatedText('receipt_table_number'), tableNumber)}
        </>
      )}
    </div>
  )

  // Build products table
  const buildProductsTable = () => {
    const productsLabel = getTranslatedText('receipt_products')
    const quantityLabel = getTranslatedText('receipt_quantity')
    const priceLabel = getTranslatedText('receipt_price')
    const currencyLabel = getReceiptCurrencyLabel()
    const isEnglish = !isArabicText(productsLabel)

    // Build header - optimized for thermal paper width - respect RTL/LTR
    const productsHeader = (
      <div className="flex-[2]">
        <span
          className={`text-sm font-bold text-black `}
          style={{
            fontFamily: isArabicText(productsLabel)
              ? "'IBM Plex Sans Arabic', sans-serif"
              : "'Roboto', sans-serif",
          }}
        >
          {productsLabel}
        </span>
      </div>
    )

    const quantityHeader = (
      <div className="w-full text-center">
        <span
          className="text-sm font-bold text-black"
          style={{
            fontFamily: isArabicText(quantityLabel)
              ? "'IBM Plex Sans Arabic', sans-serif"
              : "'Roboto', sans-serif",
          }}
        >
          {quantityLabel}
        </span>
      </div>
    )

    const priceHeader = (
      <div className="">
        <span
          className={`text-sm text-center font-bold text-black 
          `}
          style={{
            fontFamily: isArabicText(priceLabel)
              ? "'IBM Plex Sans Arabic', sans-serif"
              : "'Roboto', sans-serif",
          }}
        >
      
          {priceLabel}
        </span>
      </div>
    )

    // Build product row
    const buildProductRow = (item: any, index: number) => {
      const name =
        item?.meals?.[0]?.name ||
        item?.meal?.name ||
        item?.product?.name ||
        item?.product_name ||
        item?.meal?.product?.name ||
        item?.name ||
        'غير محدد'

      const quantity = item?.quantity?.toString() || '1'
      const price = getCorrectPrice(item)

      // Get additions/extras for this item (supports multiple formats)
      // From ordersService.fetchOrderDetails: item.modifiers (converted from additionals)
      // From OrderDetailsPage: item.meals[0].additionals
      // From other pages: item.additionals
      const additions = 
        item?.modifiers ||  // From ordersService (Order type) ✨
        (item?.meals && item.meals.length > 0 && item.meals[0]?.additionals) ||  // OrderDetailsPage format
        item?.additionals ||
        item?.extras ||
        item?.additions ||
        item?.meal?.additionals ||
        item?.product?.additionals ||
        []

      // Format additions as horizontal text (e.g., "جبنة - بطاطس - مكسرات")
      const additionsText = Array.isArray(additions) && additions.length > 0
        ? additions
            .map((add: any) => add?.name || add?.name_ar || add?.name_en || add?.title || '')
            .filter((name: string) => name)
            .join(' - ')
        : ''

      const nameWidget = (
        <div className="flex-[2]">
          <span
            className={`text-sm font-semibold text-black leading-tight `}
            style={{
              fontFamily: isArabicText(name)
                ? "'IBM Plex Sans Arabic', sans-serif"
                : "'Roboto', sans-serif",
            }}
          >
            {name}
          </span>
        </div>
      )

      const quantityWidget = (
        <div className="flex-[2] text-center">
          <span
            className="text-sm font-semibold text-black leading-tight"
            style={{ fontFamily: "'Roboto', sans-serif" }}
          >
            {quantity}
          </span>
        </div>
      )

      const priceWidget = (
        <div className={`flex-[2] flex `}>
          {isArabicText(currencyLabel) ? (
            <>
                          <span className="w-5" />

              <span
                className="text-sm font-semibold text-black leading-tight"
                style={{ fontFamily: "'Roboto', sans-serif" }}
              >
                {price}
              </span>
              <span className="w-1" />
              <span
                className="text-sm font-semibold text-black leading-tight"
                style={{ fontFamily: "'IBM Plex Sans Arabic', sans-serif" }}
              >
                {currencyLabel}
              </span>
            </>
          ) : (
            <>
              <span
                className="text-sm font-semibold text-black leading-tight"
                style={{ fontFamily: "'Roboto', sans-serif" }}
              >
                {currencyLabel}
              </span>
              <span className="w-1" />
              <span
                className="text-sm font-semibold text-black leading-tight"
                style={{ fontFamily: "'Roboto', sans-serif" }}
              >
                {price}
              </span>
            </>
          )}
        </div>
      )

      return (
        <div
          key={item?.id || item?.meal_id || index}
          className="flex flex-col pb-2"
        >
          {/* Main row: Name, Quantity, Price */}
          <div className={`flex gap-0 ${!isRTL ? 'flex-row-reverse' : ''}`}>
            {/* Arabic: Name, Quantity, Price | English: Price, Quantity, Name */}
            {isRTL
              ? [nameWidget, quantityWidget, priceWidget]
              : [priceWidget, quantityWidget, nameWidget]}
          </div>
          
          {/* Additions row - spans full width */}
          {additionsText && (
            <div className="w-full mt-0.5 px-1">
              <span
                className="text-xs text-gray-600 leading-tight"
                style={{
                  fontFamily: isArabicText(additionsText)
                    ? "'IBM Plex Sans Arabic', sans-serif"
                    : "'Roboto', sans-serif",
                }}
              >
                {additionsText}
              </span>
            </div>
          )}
        </div>
      )
    }

    return (
      <div className="flex flex-col w-full">
        <div className={`flex gap-5 ${!isRTL ? 'flex-row-reverse' : ''}`}>
          {/* Arabic: Products, Quantity, Price | English: Price, Quantity, Products */}
          {isRTL
            ? [productsHeader, quantityHeader, priceHeader]
            : [priceHeader, quantityHeader, productsHeader]}
        </div>
        <div className="h-2" />
        {items.map((item: any, index: number) => buildProductRow(item, index))}
      </div>
    )
  }

  // Build summary row
  const buildSummaryRow = (label: string, amount: string) => {
    const isEnglish = !isArabicText(label)
    const currencyLabel = getReceiptCurrencyLabel()
    const isArabicCurrency = isArabicText(currencyLabel)

    const labelWidget = (
      <span
        className="text-base font-semibold text-black leading-tight"
        style={{
          textAlign: getTextAlignment(label),
          fontFamily: isArabicText(label)
            ? "'IBM Plex Sans Arabic', sans-serif"
            : "'Roboto', sans-serif",
        }}
      >
        {label}
      </span>
    )

    const amountWidget = (
      <div className={`flex ${isEnglish ? 'justify-end' : 'justify-start'}`}>
        {isArabicCurrency ? (
          <>
            <span
              className="text-base font-semibold text-black leading-tight"
              style={{ fontFamily: "'Roboto', sans-serif" }}
            >
              {amount}
            </span>
            <span className="w-1" />
            <span
              className="text-base font-semibold text-black leading-tight"
              style={{ fontFamily: "'IBM Plex Sans Arabic', sans-serif" }}
            >
              {currencyLabel}
            </span>
          </>
        ) : (
          <>
            <span
              className="text-base font-semibold text-black leading-tight"
              style={{ fontFamily: "'Roboto', sans-serif" }}
            >
              {currencyLabel}
            </span>
            <span className="w-1" />
            <span
              className="text-base font-semibold text-black leading-tight"
              style={{ fontFamily: "'Roboto', sans-serif" }}
            >
              {amount}
            </span>
          </>
        )}
      </div>
    )

    return (
      <div
        className="flex justify-between items-center w-full"
        style={{ direction: isRTL ? 'rtl' : 'ltr' }}
      >
        {labelWidget}
        {amountWidget}
      </div>
    )
  }

  // Build notes section - displayed after products
  const buildNotesSection = () => {
    if (!notes) return null
    const notesLabel = getTranslatedText('receipt_notes')
    const isLabelArabic = isArabicText(notesLabel)
    const isNotesArabic = isArabicText(notes)
    
    return (
      <div className="flex flex-col gap-1 w-full">
        {/* Label */}
        <div className={`flex w-full ${isRTL ? 'justify-start' : 'justify-start'}`}>
          <span
            className="text-base font-semibold text-black"
            style={{
              fontFamily: isLabelArabic
                ? "'IBM Plex Sans Arabic', sans-serif"
                : "'Roboto', sans-serif",
              textAlign: isRTL ? 'right' : 'left',
            }}
          >
            {notesLabel}:
          </span>
        </div>
        {/* Notes content - wraps properly with fixed width */}
        <div 
          className="w-full"
          style={{
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
          }}
        >
          <span
            className="text-sm font-normal text-black leading-relaxed"
            style={{
              fontFamily: isNotesArabic
                ? "'IBM Plex Sans Arabic', sans-serif"
                : "'Roboto', sans-serif",
              display: 'block',
              width: '100%',
              textAlign: isRTL ? 'right' : 'left',
            }}
          >
            {notes}
          </span>
        </div>
      </div>
    )
  }

  // Build summary section - optimized for thermal paper
  const buildSummarySection = () => (
    <div className="flex flex-col gap-0.5 w-full">
      {buildSummaryRow(
        getTranslatedText('receipt_subtotal'),
        subtotal.toFixed(2)
      )}
      {/* Delivery Price - Show only if type_id is 1 (تواصيل), even if zero */}
      {shouldShowDeliveryPrice && (
        <>
          <div className="h-1.5" />
          {buildSummaryRow(
            getTranslatedText('receipt_delivery_fee') || 'رسوم التوصيل',
            deliveryFee.toFixed(2)
          )}
        </>
      )}
      {/* Table Price - Show only if type_id is 3 (محلي), even if zero */}
      {shouldShowTablePrice && (
        <>
          <div className="h-1.5" />
          {buildSummaryRow(
            getTranslatedText('receipt_table_fee') || 'رسوم الطاولة',
            tableFee.toFixed(2)
          )}
        </>
      )}
      <div className="h-1.5" />
      {buildSummaryRow(getTranslatedText('receipt_tax'), taxAmount.toFixed(2))}
      <div className="h-1.5" />
      {buildSummaryRow(
        serviceFeePercentage != null
          ? `${getTranslatedText('receipt_service_fee')} ${serviceFeePercentage}%`
          : getTranslatedText('receipt_service_fee'),
        serviceFee.toFixed(2)
      )}
      {promotionDiscount > 0 && (
        <>
          <div className="h-1.5" />
          {buildSummaryRow(
            t('offers.benefitDiscount') || getTranslatedText('receipt_discount') || 'الخصم',
            `-${promotionDiscount.toFixed(2)}`,
          )}
        </>
      )}
      {!hasPromotionDiscountRow && discountAmount != null && discountAmount > 0 && (
        <>
          <div className="h-1.5" />
          {buildSummaryRow(
            getTranslatedText('receipt_discount') || 'الخصم',
            `-${discountAmount.toFixed(2)}`
          )}
        </>
      )}
      {promotionCashback > 0 && (
        <>
          <div className="h-1.5" />
          {buildSummaryRow(
            t('offers.benefitCashback') || 'كاش باك',
            promotionCashback.toFixed(2),
          )}
        </>
      )}
      {promotionSetItemSavings > 0 && (
        <>
          <div className="h-1.5" />
          {buildSummaryRow(
            t('offers.benefitSetItemDiscount') || 'خصم على المنتج',
            `-${promotionSetItemSavings.toFixed(2)}`,
          )}
        </>
      )}
      <div className="h-1.5" />
      {buildSummaryRow(getTranslatedText('receipt_total'), total.toFixed(2))}
    </div>
  )

  // Build powered by section - optimized for thermal paper
  const buildPoweredBySection = () => (
    <div className="flex items-center justify-center gap-3">
      <span
        className="text-sm font-bold text-black"
        style={{ fontFamily: "'Roboto', sans-serif" }}
      >
        {getTranslatedText('receipt_powered_by')}
      </span>
      <img
        src={logoPrint}
        alt="Logo"
        className="h-10 w-[55px] object-contain"
      />
    </div>
  )

  return (
    <div
      className="receipt-container print-only bg-white flex flex-col items-center gap-0"
      style={{
        fontFamily: "'IBM Plex Sans Arabic', 'Roboto', sans-serif",
        width: '100%', // Responsive width - adapts to paper size
      }}
    >
      {buildHeader()}
      <DashedSeparator />
      {buildOrderNumberBox()}
      <DashedSeparator />
      {buildDetailsSection()}
      <DashedSeparator />
      {buildProductsTable()}
      {notes && (
        <>
          <DashedSeparator />
          {buildNotesSection()}
        </>
      )}
      <DashedSeparator />
      {buildSummarySection()}
      <DashedSeparator />
      {buildPoweredBySection()}
    </div>
  )
}

export default Receipt
